/**
 * Générateur — Automatisme : repérer un nombre décimal sur une demi-droite
 * graduée.
 * Attendu ID : 1065 | Niveau : 5ème | Domaine : Repérage
 * Rubrique : AUTOMATISME (programme 2025) — mobilisation en rituel court.
 * Labels (cartes exerciseur) :
 *   - Quelle abscisse ?              (saisie ; QCM à leurres calculés)
 *   - Le pas de la graduation        (saisie : quel nombre à cette graduation, quel pas)
 *   - Entre quelles graduations ?    (QCM ; V/F sur une demi-droite avec A, B, C)
 *
 * ── Plan de cartes validé (2026-09-05) ─────────────────────────────────────
 * Source : Automatisme n°27 du doc 5e + grille P1-P12 (analyse BO cycles 3-4).
 * P1 : INTRODUIT au CM1, CONSOLIDÉ au CM2 (repérer un décimal à virgule),
 * ÉTENDU en 6e (pas variés : unité en 2, 4, 5, 10), automatisme en 5e ; droite
 * graduée complète et repère FORMALISÉS en 5e (hors gen). P2 : sens installés
 * — lire une abscisse en dixièmes ; 6e — le pas non évident et la lecture
 * « rang × pas + entier » ; non installés — négatifs, coordonnées.
 * Distracteurs : le rang lu comme chiffre des dixièmes (3e graduation en
 * cinquièmes lue 2,3 au lieu de 2,6), le mauvais pas, le décalage d'une
 * graduation, « tout décimal tombe sur une graduation ». P3 : pas 0,5 / 0,25 /
 * 0,2 / 0,1 (le partage en 20 de l'éprouvette est retiré, trop dur mentalement) ; abscisses à une
 * ou deux décimales entre 0 et 10 ; produits dans les tables. P5 : la
 * demi-droite graduée avec des points nommés. P6 : aucun instrument. P7 : pas
 * de droite complète, pas de fractions non décimales. P8 : abscisse,
 * graduation, pas, unité, origine. P9 : tables et « avancer de 0,2 en 0,2 »
 * restent implicites ; placer est l'automatisme voisin (1064). P10 : constat
 * (« quel est le pas », « quelle abscisse », « sur quelle graduation »,
 * « entre quelles graduations »). P11 : le pas n'est pas toujours 0,1 ; le
 * rang d'une graduation n'est pas le chiffre des dixièmes ; un décimal ne
 * tombe pas forcément sur une graduation. P12 : rémanence forte.
 * INVARIANTS :
 *   - toute figure est CONSTRUITE depuis (start, end, div, points) et porte
 *     data-droite / data-points pour les solveurs ; les positions sont
 *     calculées en ENTIERS de graduation (rang i, 0 ≤ i ≤ div·span) ;
 *   - leurres de QCM calculés et vérifiés distincts de la bonne ; V/F : pas
 *     de crochets, mixité 1-3 faux, familles exclusives, fenêtre stricte.
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeSaisie,
} from '../../../utils';
import { imgElement } from '../statistiques/svgStats';

const ATTENDU_ID = 1065;
const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L_ABSCISSE = 'Quelle abscisse ?';
const L_PAS = 'Le pas de la graduation';
const L_ENCADRER = 'Entre quelles graduations ?';

const fr = (x) => String(Number(x.toFixed(3))).replace('.', ',');
const ORD = (n) => (n === 1 ? '1re' : `${n}e`);
// Partages de l'unité : 2, 4, 5, 10 — le partage en 20 (pas 0,05, l'éprouvette de la fiche) a été retiré
// partout (décision prof 2026-09-06 : lire 0,05 n'est pas un automatisme d'entrée de 5e).
const DIVS = [2, 4, 5, 10];
const PAS_DE = { 2: 0.5, 4: 0.25, 5: 0.2, 10: 0.1 };

// ─── Rendu : demi-droite graduée ───────────────────────────────────────────

const svgDemiDroite = (cfg) => {
    const { start, end, div } = cfg; const span = end - start;
    const U = Math.min(96, Math.floor(288 / span)); const x0 = 18; const y = 44;
    const X = (v) => x0 + (v - start) * U;
    let s = `<line x1="${x0 - 6}" y1="${y}" x2="${X(end) + 22}" y2="${y}" stroke="#1e293b" stroke-width="1.6"/>`
        + `<polygon points="${X(end) + 22},${y} ${X(end) + 14},${y - 4} ${X(end) + 14},${y + 4}" fill="#1e293b"/>`;
    const n = span * div;
    for (let i = 0; i <= n; i++) {
        const v = start + i / div; const gros = i % div === 0;
        s += `<line x1="${X(v).toFixed(1)}" y1="${y - (gros ? 9 : 5)}" x2="${X(v).toFixed(1)}" y2="${y + (gros ? 9 : 5)}" stroke="#1e293b" stroke-width="${gros ? 1.6 : 1}"/>`;
        if (gros) s += `<text x="${X(v).toFixed(1)}" y="${y + 24}" font-size="12" font-weight="700" fill="#1e293b" text-anchor="middle">${fr(v)}</text>`;
    }
    (cfg.points || []).forEach((p) => {
        s += `<circle cx="${X(p.x).toFixed(1)}" cy="${y}" r="3.6" fill="#B45309" data-point="${p.nom}"/>`
            + `<text x="${X(p.x).toFixed(1)}" y="${y - 15}" font-size="13" font-weight="700" fill="#B45309" text-anchor="middle">${p.nom}</text>`;
    });
    const w = X(end) + 30;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} 76" width="${w}" height="76" font-family="Arial" data-droite="${start};${end};${div}" data-points="${(cfg.points || []).map((p) => `${p.nom}:${p.x}`).join('|')}">${s}</svg>`;
};

/** Un point sur une graduation non entière : rang i (1 ≤ i < div·span, i non multiple de div), abscisse start + i/div. */
const tirerPoint = (div, span) => {
    const cands = []; for (let i = 1; i < div * span; i++) if (i % div !== 0) cands.push(i);
    const i = randomChoice(cands);
    return { i, x: Math.round((i / div) * 1000) / 1000, k: Math.floor(i / div), r: i % div };
};

const INTRO = (div) => `Sur cette demi-droite graduée, chaque unité est partagée en ${div}.`;

// ─── Carte 1a : quelle abscisse ? (saisie) ─────────────────────────────────

let derniereSignatureAbscisse1065 = null;
const antiRepeat = (construire, ref) => {
    let payload = construire();
    for (let t = 0; t < 8 && payload._meta.params.signature === ref.v; t++) payload = construire();
    ref.v = payload._meta.params.signature;
    return payload;
};
const refA = { v: null }; const refQ = { v: null }; const refG = { v: null }; const refE = { v: null };
void derniereSignatureAbscisse1065;

// Partage pondéré (revue 2026-09-06) : le partage en 2 n'offre que 3 abscisses (0,5 / 1,5 / 2,5),
// à 25 % il dominait les tirages ; il reste un cas de rappel à 10 %.
const tirerDivPondere = () => (Math.random() < 0.1 ? 2 : randomChoice([4, 5, 10]));

const construireAbscisseSaisie = () => {
    const div = tirerDivPondere(); const span = 3;
    const P = tirerPoint(div, span);
    const svg = svgDemiDroite({ start: 0, end: span, div, points: [{ x: P.x, nom: 'A' }] });
    const payload = makeSaisie(fr(P.x), `<p>${INTRO(div)}</p><p>Quelle est l'abscisse du point A ?</p>`, { ...opts(L_ABSCISSE), elements_additionnels: imgElement(svg, 'large') });
    payload.contenu = { ...payload.contenu, sousType: `div${div}` };
    payload._meta.params = { div, i: P.i, x: P.x, signature: `${div}-${P.i}` };
    return payload;
};
const genAbscisseSaisie = () => antiRepeat(construireAbscisseSaisie, refA);

// ─── Carte 1b : quelle abscisse ? (QCM à leurres calculés) ─────────────────

const construireAbscisseQcm = () => {
    for (let essai = 0; essai < 60; essai++) {
        const div = randomChoice([2, 4, 5]);                        // en dixièmes, le rang EST le chiffre : leurre mort
        const span = 3;
        const P = tirerPoint(div, span); const pas = PAS_DE[div];
        const x = P.x;
        const pool = [
            { v: P.k + P.r / 10, role: 'rang_dixieme' },              // 3e graduation en cinquièmes lue 2,3
            { v: P.k + P.r * (div === 4 ? 0.2 : 0.25), role: 'mauvais_pas' },
            { v: x + pas, role: 'decale_plus' },
            { v: x - pas, role: 'decale_moins' },
            { v: P.k + P.r / div * 10, role: 'pas_dix_fois' },
        ];
        const leurres = [];
        // Revue 2026-09-06 (A2) : en quarts, la bonne réponse était la seule option à deux décimales.
        // Le quart symétrique (0,25 ↔ 0,75 après le même entier) est imposé : confusion un quart / trois quarts.
        if (div === 4 && P.r % 2 === 1) leurres.push({ v: P.k + (4 - P.r) / 4, role: 'quart_symetrique' });
        shuffle(pool).forEach((c) => {
            const v = Math.round(c.v * 1000) / 1000;
            // un leurre au-delà de la demi-droite dessinée est mort (A3) : on reste dans ]0 ; end]
            if (leurres.length >= 3 || Math.abs(v - x) < 1e-9 || v <= 0 || v > span + 1e-9 || leurres.some((l) => Math.abs(l.v - v) < 1e-9)) return;
            leurres.push({ v, role: c.role });
        });
        if (leurres.length < 3) continue;
        const options = shuffle([fr(x), ...leurres.map((l) => fr(l.v))]);
        const svg = svgDemiDroite({ start: 0, end: span, div, points: [{ x, nom: 'A' }] });
        const payload = makeQcm(options, fr(x), `<p>${INTRO(div)}</p><p>Quelle est l'abscisse du point A ?</p>`, { ...opts(L_ABSCISSE), elements_additionnels: imgElement(svg, 'large') });
        payload.contenu = { ...payload.contenu, sousType: `div${div}` };
        payload._meta.params = { div, i: P.i, x, roles: leurres.map((l) => l.role), signature: `${div}-${P.i}` };
        return payload;
    }
    throw new Error('gen_1065 : QCM abscisse sans leurres');
};
const genAbscisseQcm = () => antiRepeat(construireAbscisseQcm, refQ);

// ─── Carte 2b : quel nombre à cette graduation ? (saisie) ──────────────────

// La saisie du pas (4 réponses possibles, sous le seuil de variété du harnais) est une question de ce format.
const construireGraduationSaisie = () => {
    const cas = randomChoice(['apres', 'avant', 'rang', 'pas']);
    const div = randomChoice(DIVS); const span = 3; const pas = PAS_DE[div];
    let texte; let reponse; let suffixe = ''; let params;
    if (cas === 'apres') {
        const k = randInt(0, span - 1); const r = randInt(1, div - 1);
        texte = `<p>${INTRO(div)}</p><p>Quel nombre est repéré par la ${ORD(r)} petite graduation après ${k} ?</p>`;
        reponse = fr(k + r * pas); params = { k, r };
    } else if (cas === 'avant') {
        const k = randInt(1, span);
        texte = `<p>${INTRO(div)}</p><p>Quel nombre est repéré par la graduation située juste avant ${k} ?</p>`;
        reponse = fr(k - pas); params = { k };
    } else if (cas === 'rang') {
        const k = randInt(0, span - 1); const r = randInt(1, div - 1);
        texte = `<p>${INTRO(div)}</p><p>Le nombre ${fr(k + r * pas)} se trouve sur une petite graduation après ${k}. Laquelle ? Donne son rang.</p>`;
        reponse = String(r); suffixe = 'e graduation'; params = { k, r };
    } else {
        texte = `<p>${INTRO(div)}</p><p>Quel est le pas de cette graduation, c'est-à-dire l'écart entre deux graduations voisines ?</p>`;
        reponse = fr(pas); params = {};
    }
    const svg = svgDemiDroite({ start: 0, end: span, div });
    const payload = makeSaisie(reponse, texte, { ...opts(L_PAS), suffixe, elements_additionnels: imgElement(svg, 'large') });
    payload.contenu = { ...payload.contenu, sousType: cas };
    payload._meta.params = { cas, div, ...params, reponse, signature: `${cas}-${div}-${Object.values(params).join('x')}` };
    return payload;
};
const genGraduationSaisie = () => antiRepeat(construireGraduationSaisie, refG);

// ─── Carte 3a : entre quelles graduations ? (QCM) ──────────────────────────

// Revue 2026-09-06 (A3) : le leurre « chiffre des dixièmes lu comme rang » doit désigner une graduation
// qui EXISTE sur la figure (« 7e petite graduation » en demis s'éliminait d'un coup d'œil, 71 % des tirages).
// Sinon : un rang existant différent du vrai ; en demis (une seule petite graduation), erreur d'unité.
const rangFaux = (div, k, chiffre, rVrai) => {
    if (chiffre !== null && chiffre < div && chiffre !== rVrai) return { r: chiffre, k };
    const existants = []; for (let q = 1; q < div; q++) if (q !== rVrai) existants.push(q);
    if (existants.length) return { r: randomChoice(existants), k };
    return { r: 1, k: k === 0 ? 1 : k - 1 };
};
const leurreRangOui = (div, k, chiffre, rVrai) => {
    const f = rangFaux(div, k, chiffre, rVrai);
    return `Oui : exactement sur la ${ORD(f.r)} petite graduation après ${f.k}.`;
};

const construireEncadrerQcm = () => {
    const div = randomChoice([2, 4, 5]); const span = 3; const pas = PAS_DE[div];
    const sur = Math.random() < 0.35;
    const k = randInt(0, span - 1);
    let x; let bonne; let leurres;
    if (sur) {
        const r = randInt(1, div - 1); x = Math.round((k + r * pas) * 100) / 100;
        bonne = `Oui : exactement sur la ${ORD(r)} petite graduation après ${k}.`;
        leurres = [`Non : il se situe entre ${fr(k + (r - 1) * pas)} et ${fr(k + r * pas)}.`, leurreRangOui(div, k, Math.round(x * 10) % 10 || 10, r), `Non : il se situe entre ${fr(k + r * pas)} et ${fr(k + (r + 1) * pas)}.`];
    } else {
        // un dixième qui ne tombe pas sur la graduation
        const cands = []; for (let d = 1; d <= 9; d++) if (Math.abs(Math.round((d / 10) / pas) * pas - d / 10) > 1e-9) cands.push(d);
        const d = randomChoice(cands); x = k + d / 10;
        const r = Math.floor((d / 10) / pas + 1e-9);            // graduation juste avant
        const bas = fr(k + r * pas); const haut = fr(k + (r + 1) * pas);
        bonne = `Non : il se situe entre ${bas} et ${haut}.`;
        // Revue 2026-09-06 (A3) : l'intervalle décalé reste DANS la figure (au-delà de 3, on prend celui du dessous).
        const decale = k + (r + 2) * pas > span + 1e-9
            ? `Non : il se situe entre ${fr(k + (r - 1) * pas)} et ${bas}.`
            : `Non : il se situe entre ${haut} et ${fr(k + (r + 2) * pas)}.`;
        leurres = [leurreRangOui(div, k, d, null), decale, `Oui : exactement sur la graduation ${fr(x)}.`];
    }
    const uniques = new Set([bonne, ...leurres]); if (uniques.size < 4) return construireEncadrerQcm();
    const svg = svgDemiDroite({ start: 0, end: span, div });
    const payload = makeQcm(shuffle([bonne, ...leurres]), bonne, `<p>${INTRO(div)}</p><p>Le nombre ${fr(x)} tombe-t-il sur une graduation ? Sinon, entre lesquelles se situe-t-il ?</p>`, { ...opts(L_ENCADRER), elements_additionnels: imgElement(svg, 'large') });
    payload.contenu = { ...payload.contenu, cadence: 'raisonnement', sousType: sur ? 'sur' : 'entre' };
    payload._meta.params = { div, x, sur, signature: `${div}-${x}` };
    return payload;
};
const genEncadrerQcm = () => antiRepeat(construireEncadrerQcm, refE);

// ─── Carte 3b : V/F sur une demi-droite avec A, B, C ───────────────────────

let dernieresLignesVfDroite1065 = [];

const genLectureVf = () => {
    for (let essai = 0; essai < 160; essai++) {
        const div = randomChoice([2, 4, 5, 10]); const span = 3; const pas = PAS_DE[div];
        const cands = []; for (let i = 1; i < div * span; i++) if (i % div !== 0) cands.push(i);
        if (cands.length < 3) continue;
        const rangs = shuffle(cands).slice(0, 3).sort((a, b) => a - b);
        if (rangs[1] - rangs[0] < 2 || rangs[2] - rangs[1] < 2) continue;      // étiquettes espacées
        const noms = ['A', 'B', 'C'];
        const pts = rangs.map((i, n) => ({ nom: noms[n], i, x: Math.round((i / div) * 1000) / 1000, k: Math.floor(i / div), r: i % div }));
        const P = randomChoice(pts); const Q = randomChoice(pts.filter((p) => p !== P));
        const autrePas = PAS_DE[randomChoice(DIVS.filter((d) => d !== div))];
        // un dixième hors graduation (si le pas n'est pas 0,1)
        const horsCands = []; for (let d = 1; d <= 9; d++) if (Math.abs(Math.round((d / 10) / pas) * pas - d / 10) > 1e-9) horsCands.push(d);
        const hors = horsCands.length ? randInt(0, span - 1) + randomChoice(horsCands) / 10 : null;
        const surGrad = Math.round((randInt(0, span - 1) + randInt(1, div - 1) * pas) * 100) / 100;
        const candidates = [
            { l: `Le pas de cette graduation est ${fr(pas)}.`, v: true },
            { l: `Le pas de cette graduation est ${fr(autrePas)}.`, v: false },
            { l: `L'abscisse de ${P.nom} est ${fr(P.x)}.`, v: true },
            { l: `L'abscisse de ${P.nom} est ${fr(P.k + P.r / 10)}.`, v: div === 10 },                       // rang lu comme dixième
            { l: `${P.nom} est la ${ORD(P.r)} petite graduation après ${P.k}.`, v: true },
            { l: (() => { const f = rangFaux(div, P.k, null, P.r); return `${P.nom} est la ${ORD(f.r)} petite graduation après ${f.k}.`; })(), v: false },
            { l: `${P.nom} est plus loin de l'origine que ${Q.nom}.`, v: P.x > Q.x },
            { l: `Le nombre ${fr(surGrad)} tombe exactement sur une graduation.`, v: true },
            ...(hors !== null ? [{ l: `Le nombre ${fr(hors)} tombe exactement sur une graduation.`, v: false }] : []),
        ];
        const fausses = shuffle(candidates.filter((it) => !it.v)); const vraies = shuffle(candidates.filter((it) => it.v));
        const nbFaux = randInt(1, 3);
        const lignes = shuffle([...fausses.slice(0, nbFaux), ...vraies.slice(0, 4 - nbFaux)]);
        if (lignes.length !== 4) continue;
        const famille = (l) => (/^Le pas/.test(l) ? 'pas' : /^L'abscisse de/.test(l) ? 'abscisse' : /petite graduation après/.test(l) ? 'rang' : /tombe exactement/.test(l) ? 'graduation' : l);
        if (new Set(lignes.map((it) => famille(it.l))).size !== 4) continue;
        if (essai > 120) dernieresLignesVfDroite1065 = [];
        if (lignes.some((it) => dernieresLignesVfDroite1065.includes(it.l))) continue;
        dernieresLignesVfDroite1065 = lignes.map((it) => it.l);
        const svg = svgDemiDroite({ start: 0, end: span, div, points: pts.map((p) => ({ x: p.x, nom: p.nom })) });
        const payload = makeVraiFaux(
            lignes.map((it) => it.l),
            lignes.map((it) => (it.v ? 0 : 1)),
            `<p>${INTRO(div)} Pour chaque affirmation, dis si elle est vraie ou fausse.</p>`,
            { ...opts(L_ENCADRER), elements_additionnels: imgElement(svg, 'large') },
        );
        payload.contenu = { ...payload.contenu, cadence: 'raisonnement' };
        payload._meta.params = { div, points: pts.map((p) => p.x) };
        return payload;
    }
    throw new Error('gen_1065 : V/F droite sans tirage après 160 essais');
};

// (Le V/F de pur rappel « Pas et abscisse : vrai ou faux ? » a été retiré à la demande du prof, 2026-09-05.)

// ─── Registre ──────────────────────────────────────────────────────────────

const GENERATORS = {
    abscisse_saisie: genAbscisseSaisie,
    abscisse_qcm_unique: genAbscisseQcm,
    pas_graduation_saisie: genGraduationSaisie,
    encadrer_qcm_unique: genEncadrerQcm,
    encadrer_tableau_vf: genLectureVf,
};

const FORMAT_LABELS = { saisie: 'Saisie', qcm_unique: 'QCM', graduation_saisie: 'Quel nombre ? (saisie)', tableau_vf: 'Vrai/Faux' };

const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter((k) => k.startsWith(`${prefix}_`))
        .map((k) => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'abscisse', label: L_ABSCISSE, formatsList: buildFormats('abscisse') },
    { id: 'pas', label: L_PAS, formatsList: buildFormats('pas') },
    { id: 'encadrer', label: L_ENCADRER, formatsList: buildFormats('encadrer') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter((k) => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
